import { Fragment } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { eur, dt } from "@/lib/api";
import { Button } from "@/components/ui/button";

const n = (x) => (x ? eur(x) : "");
const TH = ({ children, r }) => <th className={`whitespace-nowrap px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider ${r ? "text-right" : "text-left"}`}>{children}</th>;
const TD = ({ children, r, className = "" }) => <td className={`px-3 py-2 ${r ? "text-right font-mono tabular-nums" : ""} ${className}`}>{children}</td>;

export const Sheet = ({ head, children, foot, testid }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
    <table className="w-full min-w-[720px] text-sm" data-testid={testid}>
      <thead className="bg-slate-900 text-slate-100"><tr>{head}</tr></thead>
      <tbody className="divide-y divide-slate-100">{children}</tbody>
      {foot && <tfoot className="border-t-2 border-slate-900 bg-slate-50 font-semibold">{foot}</tfoot>}
    </table>
  </div>
);

const Cod = ({ c }) => <span className="mr-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">{c}</span>;

export const Giornale = ({ rep, nomi, chiusi = [], onEdit, onDelete }) => (
  <Sheet testid="coge-giornale" head={<><TH>N.</TH><TH>Data</TH><TH>Causale e conti</TH><TH r>Dare</TH><TH r>Avere</TH>{onEdit && <TH />}</>}
    foot={<tr><TD /><TD /><TD>Totali del periodo</TD><TD r>{eur(rep.dare)}</TD><TD r>{eur(rep.avere)}</TD>{onEdit && <TD />}</tr>}>
    {rep.items.length === 0 && <tr><TD className="text-slate-500">—</TD><TD /><TD className="text-slate-500">Nessuna registrazione nel periodo</TD><TD /><TD /></tr>}
    {rep.items.map((s) => (
      <Fragment key={s.id}>
        <tr className="bg-sky-50/60" data-testid={`coge-scr-${s.id}`}>
          <TD className="font-mono text-xs text-slate-500">{s.numero}</TD>
          <TD className="whitespace-nowrap">{dt(s.data)}</TD>
          <TD><b className="text-slate-900">{s.causale_nome}</b> <span className="text-slate-600">· {s.descrizione}</span>
            {s.auto && <span className="ml-2 rounded bg-white px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-700 ring-1 ring-sky-200">automatica</span>}
            {s.rif_auto && <span className="ml-2 rounded bg-white px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-700 ring-1 ring-amber-200">corretta</span>}</TD>
          <TD /><TD />
          {onEdit && <TD className="whitespace-nowrap text-right">{!(s.chiusura || s.apertura || chiusi.includes(Number(s.data.slice(0, 4)))) && <>
            {!s.liq && <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`coge-edit-${s.id}`} onClick={() => onEdit(s)}><Pencil className="h-3.5 w-3.5" /></Button>}
            {!s.auto && <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`coge-del-${s.id}`} onClick={() => onDelete(s)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>}</>}</TD>}
        </tr>
        {s.righe.map((x, i) => (
          <tr key={i} className="text-slate-700">
            <TD /><TD />
            <TD className={x.avere ? "pl-12" : "pl-6"}><Cod c={x.conto} />{nomi[x.conto] || x.conto}</TD>
            <TD r>{n(x.dare)}</TD><TD r>{n(x.avere)}</TD>{onEdit && <TD />}
          </tr>
        ))}
      </Fragment>
    ))}
  </Sheet>
);

export const Mastrino = ({ rep }) => (
  <Sheet testid="coge-mastrino" head={<><TH>Data</TH><TH>N.</TH><TH>Descrizione</TH><TH r>Dare</TH><TH r>Avere</TH><TH r>Saldo</TH></>}
    foot={<tr><TD /><TD /><TD>Totali e saldo finale</TD><TD r>{eur(rep.dare)}</TD><TD r>{eur(rep.avere)}</TD><TD r>{eur(rep.saldo_finale)}</TD></tr>}>
    <tr className="bg-sky-50/60"><TD /><TD /><TD className="font-semibold">Saldo iniziale al {dt(rep.dal)}</TD><TD /><TD /><TD r className="font-semibold">{eur(rep.saldo_iniziale)}</TD></tr>
    {rep.items.map((r) => (
      <tr key={r.id} data-testid={`coge-mas-${r.id}`}>
        <TD className="whitespace-nowrap">{dt(r.data)}</TD><TD className="font-mono text-xs text-slate-500">{r.numero}</TD>
        <TD><span className="text-slate-500">{r.causale_nome} ·</span> {r.descrizione}</TD>
        <TD r>{n(r.dare)}</TD><TD r>{n(r.avere)}</TD><TD r className="font-semibold">{eur(r.saldo)}</TD>
      </tr>
    ))}
  </Sheet>
);

export const Verifica = ({ rep }) => (
  <Sheet testid="coge-verifica" head={<><TH>Codice</TH><TH>Conto</TH><TH>Mastro</TH><TH r>Dare</TH><TH r>Avere</TH><TH r>Saldo</TH></>}
    foot={<tr><TD /><TD>Totali {Math.abs(rep.dare - rep.avere - rep.risultato_precedente) < 0.01 ? "· quadrato" : "· NON quadrato"}</TD><TD /><TD r>{eur(rep.dare)}</TD><TD r>{eur(rep.avere + rep.risultato_precedente)}</TD><TD /></tr>}>
    {!!rep.risultato_precedente && <tr className="bg-amber-50/60 italic"><TD /><TD>Risultato esercizi precedenti non chiusi</TD><TD /><TD /><TD r>{eur(rep.risultato_precedente)}</TD><TD /></tr>}
    {rep.items.map((i) => (
      <tr key={i.codice} data-testid={`coge-ver-${i.codice}`}>
        <TD><Cod c={i.codice} /></TD><TD className="font-medium text-slate-900">{i.nome}</TD><TD className="text-slate-500">{i.mastro}</TD>
        <TD r>{n(i.dare)}</TD><TD r>{n(i.avere)}</TD><TD r className={`font-semibold ${i.saldo < 0 ? "text-rose-700" : ""}`}>{eur(i.saldo)}</TD>
      </tr>
    ))}
  </Sheet>
);

const Sez = ({ title, items, extra = [], total, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={testid}>
    <div className="border-b border-slate-200 bg-slate-900 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-100">{title}</div>
    <div className="divide-y divide-slate-100 text-sm">
      {items.map((i) => <div key={i.codice} className="flex justify-between gap-3 px-4 py-2"><span><Cod c={i.codice} />{i.nome}</span><span className="font-mono tabular-nums">{eur(i.saldo)}</span></div>)}
      {extra.map(([l, v]) => <div key={l} className="flex justify-between gap-3 bg-amber-50/60 px-4 py-2 italic"><span>{l}</span><span className="font-mono tabular-nums">{eur(v)}</span></div>)}
      {items.length + extra.length === 0 && <div className="px-4 py-3 text-slate-500">Nessun saldo</div>}
    </div>
    <div className="flex justify-between border-t-2 border-slate-900 bg-slate-50 px-4 py-2.5 text-sm font-semibold"><span>Totale</span><span className="font-mono tabular-nums">{eur(total)}</span></div>
  </div>
);

export const Bilancio = ({ rep }) => (
  <div className="space-y-6">
    <div className={`rounded-xl border p-5 ${rep.utile >= 0 ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid="coge-utile">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">{rep.utile >= 0 ? "Utile del periodo" : "Perdita del periodo"}</div>
      <div className="mt-1 font-mono text-3xl font-semibold">{eur(rep.utile)}</div>
      <div className="mt-1 text-xs text-slate-500">Ricavi {eur(rep.totali.R)} − costi {eur(rep.totali.C)} · prospetto gestionale, non è il bilancio CEE</div>
    </div>
    <h3 className="font-display text-lg font-bold text-slate-900">Stato patrimoniale</h3>
    <div className="grid gap-4 lg:grid-cols-2">
      <Sez title="Attività" items={rep.sezioni.A} total={rep.totali.A} testid="coge-sp-attivo" />
      <Sez title="Passività e patrimonio netto" items={rep.sezioni.P} total={rep.totale_passivo} testid="coge-sp-passivo"
        extra={[...(rep.risultato_precedente ? [["Risultati periodi precedenti", rep.risultato_precedente]] : []), ["Utile (perdita) del periodo", rep.utile]]} />
    </div>
    <h3 className="font-display text-lg font-bold text-slate-900">Conto economico</h3>
    <div className="grid gap-4 lg:grid-cols-2">
      <Sez title="Costi" items={rep.sezioni.C} total={rep.totali.C} testid="coge-ce-costi" />
      <Sez title="Ricavi" items={rep.sezioni.R} total={rep.totali.R} testid="coge-ce-ricavi" />
    </div>
  </div>
);
